import React, {useEffect, useRef} from 'react';
import {cacheClear, commafyPlural, debounce, funcOrValOr, MS_MIN, objMap} from '../tools/CommonJS';
import {componentInit, dataSingletonsClear} from '../tools/CommonReact';
import {icn} from '../tools/UIIcons';
import {Button} from '../tools/Inputs';
import {TimedActionMessageBox} from '../tools/UIDialog';
import {UIComboBox} from '../tools/UIComboBox';
import {appCtx, genTooltip, queryOrErr, useFetchData, useFetchDataSingleton} from './AppContext';
import {c_accessFull, c_accessView, dispGroupName, groupNameAll} from './CommonUIData';
import {FilterEntry, TitleWithBadges} from './CommonUIComponenets';
import {mockData} from './MockData';
import {setRumUser} from './rum';

export const c_accessDisp = {[c_accessFull]: 'Full', [c_accessView]: 'View'};
export const c_accessOpts = Object.entries(c_accessDisp).map(([k, h]) => ({k, h}));

export function useUser({onLoading} = {}) {
	const {rsp, refresh} = useFetchDataSingleton({
		api: '/api/v2/perms_get-user-perms',
		socket: 'roles_updated',
		onLoading,
		resetOnLastUnuse: true,
		alt: mockData.getUserPerms,
		onData: r => (setRumUser(r), r)
	});

	const signOut = () => {
		dataSingletonsClear();
		cacheClear();
		appCtx.auth?.signOut();
	};

	return {user: rsp && {...rsp.oUser, perms: rsp.perms}, signOut, refreshUser: refresh};
}


export function useGroups({onLoading} = {}) {
	const {rsp: groups, refresh: refreshGroups} = useFetchDataSingleton({api: '/api/v2/groups_list', socket: 'groups_updated', onLoading, alt: mockData.groupList});

	return {groups: groups?.data, refreshGroups};
}

// groups is optional - supply it if you have it anyway to save some cycles.
export function FilterEntryInputGroup({
	groups = undefined, groupId, onChange, addClear = undefined, className = '', classNameColor = undefined, classNameEntry = undefined,
	optsFilter = undefined, flat = false, disabled = undefined, noAllGrp = undefined, btnAfter = undefined, inputProps = undefined, placeholder = undefined,
	item0 = undefined
}) {
	if (!groups) ({groups} = useGroups());
	const grp = groups?.find(g => g.groupId === groupId);
	if (optsFilter)
		groups = groups.filter(optsFilter);
	return <FilterEntry
		className={classNameEntry ?? (flat && 'flex items-center relative')} classNameColor={classNameColor} title='Device Group' field='groupId' {...{disabled}}
		info={grp && <div className={`grow text-end text-xs text-subtitle1 ${flat && 'absolute top-0 right-0 translate-y-[-110%]'}`}>
			{commafyPlural(grp.totalDevices, 'device')}</div>}>
		<div className='flex items-center'>
			<div className='grow'>
				<InputGroup {...{groups, groupId, onChange, addClear, className, disabled, noAllGrp, inputProps, placeholder, item0}}/>
			</div>
			{btnAfter}
		</div>
	</FilterEntry>;
}

export const FilterEntryGroupUngrouped = ({
	groups, inp, onChange, comb
}) => (
	<FilterEntryInputGroup
		{...{groups, onChange}} groupId={inp.groupId} addClear disabled={funcOrValOr(comb.disabled, {inp})}
		optsFilter={comb.optsFilter}
		inputProps={{...inp.ungrouped && {readOnly: true, onClick: () => onChange('ungrouped', false)}}}
		btnAfter={
			<Button
				className='shrink text-2xs p-1 ml-2 w-[5.725rem] rounded-lg bg-bg1/40 h-9 '
				onClick={() => (onChange('ungrouped', !inp.ungrouped))} {...genTooltip('Show only devices that are not in any custom Device Group.')}>
				<div className={`whitespace-wrap text-center py-0.5 h-full px-0.5 !text-wrap ${
					inp.ungrouped ? 'bg-bg1' : 'bg-transparent text-subtitle1 hover:text-title'} rounded`}>
					Ungrouped Devices
				</div>
			</Button>}/>
);

// groups is optional - supply it if you have it anyway to save some cycles.
export function InputGroup({
	groups = undefined, groupId = undefined, addClear = true, noAllGrp = false, item0 = undefined, ...props
}) {
	if (!groups) ({groups} = useGroups());
	return (
		<UIComboBox
			selectMode enableSearch field='groupId' item0={item0 ?? (!noAllGrp && groupNameAll)}
			{...{addClear, ...props}} stylePopPanel={{width: '20rem'}}
			value={groupId} opts={groups?.map(g => ({h: dispGroupName(g), k: g.groupId, g}))}
			rowRenderer={TitleWithBadgesGroup}/>
	);
}

export const TitleWithBadgesGroup = ({g, h}) => (
	<TitleWithBadges
		dataTest='input-group'
		className='w-full' h={h} badges={[
			...g? [{v: g.totalDevices, fTip: `$1 device${g.totalDevices.plural}`}] : [],
			...g?.isBuiltIn? [{v: icn.lock, fTip: 'Built-in Group', className: 'px-0.5'}] : []
		]}/>
);

export function useDomains({onLoading, onData, api} = {}) {
	const {rsp: domains, refresh: refreshDomains} = useFetchData({
		api: api,
		cacheMins: true,
		onLoading,
		onData: r => (onData?.(r.domains) ?? r.domains ?? r),
		alt: mockData.getDomains,
	});

	return {domains, refreshDomains};
}


export function InputDomain({
	domain, onChange, addClear, className = '', api = '/api/v2/domains_list', requireDomain = false
}) {
	const {domains} = useDomains({api});
	let emptyDomainCount = 0;
	const opts = domains?.map(d => ({h: d || `(no domain${emptyDomainCount++? ` - ${emptyDomainCount}` : ''})`, k: d}));

	useEffect(() => {
		requireDomain && domain === undefined && domains && onChange('domain', domains[0]);
	}, [domain, domains]);

	return <UIComboBox
		selectMode field='domain' value={domain} placeholder={requireDomain? undefined : 'All'} noCount
		{...{opts, onChange, addClear, className}}/>;
}


export const useFeatureFlags = ({onLoading} = {}) => useFetchDataSingleton({
	resetOnLastUnuse: true,
	onLoading,
	noErrMsg: true,
	api: '/api/v2/feature-flags_get',
	alt: mockData.feature_flags_get,
	pollMs: 10 * MS_MIN,
	onData: response => withDevFlags(objMap(response.data, (_name, flag) => flag.value)),          // convert into {flag: value, ...}
});

// Dev server only: VITE_FF_ON=ff_a,ff_b (e.g. in .env.local) forces those flags on. Stripped from builds.
const devFlagsOn = import.meta.env.DEV ? (import.meta.env.VITE_FF_ON ?? '').split(',').map(flagName => flagName.trim()).filter(Boolean) : [];

const withDevFlags = flags => !devFlagsOn.length ? flags :
	{...flags, ...Object.fromEntries(devFlagsOn.map(flagName => [flagName, {...flags?.[flagName], enabled: true}]))};


export function useTableColumns() {
	const {rsp: configs} = useFetchDataSingleton({
		api: '/api/v2/pinned_get',
		params: {scope: 'table_columns'},
		alt: {pinned: {}},
		init: {},
		onData: r => Object.assign(configs, r.pinned),
	});

	useTableColumns.debouncedSave ??= debounce(() => queryOrErr({
		api: '/api/v2/pinned_set',
		params: {scope: 'table_columns', pinned: configs},
		alt: {},
		noErrMsg: true,
	}), 5000);

	return {
		getTableStore: storeKey => (configs[storeKey] ??= {}),
		onChangeStore: useTableColumns.debouncedSave,
	};
}


export function useCheckRefresh() {
	const vals = useRef({}).current;
	const {rsp} = useFetchData({api: '/api/v2/version_timestamp', noErrMsg: true, pollMs: 10 * MS_MIN, alt: mockData.version_timestamp});
	if (!rsp?.timestamp || !componentInit(vals, [rsp]))
		return;            // componentInit ensures this triggers only when rsp changes, i.e. every 10 minutes.
	if (!useCheckRefresh.prevRfsTm)
		useCheckRefresh.prevRfsTm = rsp.timestamp;
	if (useCheckRefresh.prevRfsTm === rsp.timestamp)
		return;

	const durationSecs = 60;
	setTimeout(() =>
		appCtx.setDlg({Elm: TimedActionMessageBox,
			msg: `A new version is available. The user interface will auto-refresh in ${durationSecs} seconds.`,
			durationSecs,
			className: 'dlg-xs text-base',
			btnOk: 'Refresh',
			onOk: () => window.location.reload()}));
}
